// Video configuration
let FPS = null; // No default - must be set explicitly
export const getFPS = () => FPS;
export const setFPS = (newFPS) => {
  if (newFPS === null || newFPS === undefined) {
    throw new Error('FPS cannot be null or undefined');
  }
  FPS = newFPS;
  // Update derived constants
  FRAME_DURATION = 1 / FPS;
  FRAME_TOLERANCE = FRAME_DURATION / 2;
};

let FRAME_DURATION = null;
let FRAME_TOLERANCE = null;

export { FRAME_DURATION, FRAME_TOLERANCE };

// Video dimensions
export const VIDEO_WIDTH = 1280;
export const VIDEO_HEIGHT = 720;

// Timeline configuration
export const TIMELINE_HEIGHT = 40;
export const TIMELINE_VIEWPORT_WIDTH = 1280;
export const TIMELINE_WINDOW = 20; // 20 second window for high temporal resolution

// Zoom levels and time windows
export const ZOOM_LEVELS = [null, 0.05, 0.25, 0.5, 1, 2];
export const TIME_WINDOWS = [null, 300, 60, 30, 15, 7.5]; // null means full video duration

// Annotation types
export const ANNOTATION_TYPES = ['stroke', 'bounce', 'in-flight', 'net'];

// Sentinel for "ball not in frame" (negative training label)
export const BALL_NOT_IN_FRAME = -9999;

// Sentinel a strokes file uses in bounce_frame when the stroke was never followed by a
// bounce, which is what makes it a volley.
export const NO_BOUNCE_FRAME = -9999;

// Bounding box format for player detection (ResNet50)
// Format: { boundingBox: { x1, y1, x2, y2 } }
// Coordinates should be in native video coordinate space
export const BOUNDING_BOX_FORMAT = 'nested';

// Court keypoint labels (A-R mapping)
export const COURT_KEYPOINT_LABELS = [
  'A', 'B', 'C', 'D', 'E',  // Bottom baseline (left to right)
  'F', 'G', 'H',            // Bottom service line (left to right)
  'I', 'J', 'K',            // Top service line (left to right)
  'L', 'M', 'N', 'O', 'P',  // Top baseline (left to right)
  'Q', 'R'                   // Center line endpoints
];

// Court keypoint types
export const COURT_KEYPOINT_TYPES = {
  'A': 'baseline', 'B': 'baseline', 'C': 'baseline', 'D': 'baseline', 'E': 'baseline',
  'F': 'service_line', 'G': 'service_line', 'H': 'service_line',
  'I': 'service_line', 'J': 'service_line', 'K': 'service_line',
  'L': 'baseline', 'M': 'baseline', 'N': 'baseline', 'O': 'baseline', 'P': 'baseline',
  'Q': 'baseline', 'R': 'baseline'
};

// Default court positions (proper tennis court proportions with 2.89:1 width:height ratio)
export const DEFAULT_COURT_POSITIONS = {
  'A': { x: 540, y: 650, outOfFrame: false },   // Bottom left
  'B': { x: 580, y: 650, outOfFrame: false },   // Bottom left service
  'C': { x: 640, y: 650, outOfFrame: false },   // Bottom center
  'D': { x: 700, y: 650, outOfFrame: false },   // Bottom right service
  'E': { x: 740, y: 650, outOfFrame: false },   // Bottom right
  'F': { x: 580, y: 500, outOfFrame: false },   // Bottom service left (aligned with B)
  'G': { x: 640, y: 500, outOfFrame: false },   // Bottom service center
  'H': { x: 700, y: 500, outOfFrame: false },   // Bottom service right (aligned with D)
  'I': { x: 580, y: 220, outOfFrame: false },   // Top service left (aligned with M)
  'J': { x: 640, y: 220, outOfFrame: false },   // Top service center
  'K': { x: 700, y: 220, outOfFrame: false },   // Top service right (aligned with O)
  'L': { x: 540, y: 70, outOfFrame: false },    // Top left
  'M': { x: 580, y: 70, outOfFrame: false },    // Top left service
  'N': { x: 640, y: 70, outOfFrame: false },    // Top center
  'O': { x: 700, y: 70, outOfFrame: false },    // Top right service
  'P': { x: 740, y: 70, outOfFrame: false },    // Top right
  'Q': { x: 540, y: 360, outOfFrame: false },   // Center line left
  'R': { x: 740, y: 360, outOfFrame: false }    // Center line right
};

// Player colors
export const PLAYER_COLORS = {
  red: '#ff5555',
  green: '#50fa7b',
};

// Keyboard shortcuts
export const KEYBOARD_SHORTCUTS = {
  PLAY_PAUSE: ' ',
  FRAME_BACK: 'ArrowLeft',
  FRAME_FORWARD: 'ArrowRight',
  SKIP_FRAMES: 'Shift',
  SWITCH_MODE: 'm',
  REMOVE_ANNOTATION: ['x', 'X'],
  SAVE_ANNOTATIONS: ['s', 'S'],
  COURT_MODE: 'c',
  BALL_NOT_IN_FRAME: 'n',
  CYCLE_CUSTOM_LABEL: 'g',
  TOGGLE_FLAG_FOR_REVIEW: 'r',
  SET_STROKE_IN: 'i',
  SET_STROKE_OUT: 'o',
  // Rally mode: one key starts a rally and then ends it, then 1/2 score it.
  RALLY_MARK: 'enter',
  RALLY_WINNER_NEAR: '1',
  RALLY_WINNER_FAR: '2',
};

// Line confidence written to a stroke whose in/out call has been set by hand
export const MANUAL_LINE_CONFIDENCE = 0.9;

// File upload
export const SUPPORTED_VIDEO_FORMATS = ['.mp4', '.webm', '.ogg'];
export const SUPPORTED_ANNOTATION_FORMATS = ['.json'];

// Default values
export const DEFAULT_ZOOM = 1;
export const DEFAULT_PAN = { x: 0, y: 0 };
export const DEFAULT_TIMELINE_ZOOM = 1;
export const DEFAULT_TIMELINE_ZOOM_CENTER = 0; 